/**
 * GoogleCalendarSync - Service de synchronisation bidirectionnelle avec Google Calendar
 *
 * Fonctionnalités:
 * - Création automatique d'événements Calendar quand une tâche est créée
 * - Mise à jour automatique quand une tâche est modifiée
 * - Suppression automatique quand une tâche est supprimée
 * - Gestion du mapping ID local <-> ID Google
 */

import googleCalendarService from './googleCalendar';

// Helper pour extraire le texte d'une instruction (peut être string ou objet)
const getInstructionText = (instruction) => {
  if (!instruction) return '';
  if (typeof instruction === 'string') return instruction;
  if (typeof instruction === 'object' && instruction.text) return instruction.text;
  if (typeof instruction === 'object' && instruction.name) return instruction.name;
  return '';
};

// Cache des mappings locaux -> Google event IDs
const eventMapping = new Map();
const MAPPING_STORAGE_KEY = 'adhd-calendar-mapping';

// Charger le mapping depuis localStorage
function loadMapping() {
  try {
    const data = localStorage.getItem(MAPPING_STORAGE_KEY);
    if (data) {
      const parsed = JSON.parse(data);
      Object.entries(parsed).forEach(([k, v]) => eventMapping.set(k, v));
    }
  } catch (e) {
    console.warn('Erreur chargement mapping Calendar:', e);
  }
}

// Sauvegarder le mapping
function saveMapping() {
  try {
    const obj = Object.fromEntries(eventMapping);
    localStorage.setItem(MAPPING_STORAGE_KEY, JSON.stringify(obj));
  } catch (e) {
    console.warn('Erreur sauvegarde mapping Calendar:', e);
  }
}

// Initialiser au chargement
loadMapping();

/**
 * Vérifier si la sync Google Calendar est disponible
 */
function isCalendarAvailable() {
  return !!(window.gapi?.client?.getToken?.() && window.gapi?.client?.calendar);
}

/**
 * Créer un événement dans Google Calendar à partir d'une tâche locale
 */
async function createCalendarEvent(task, date) {
  if (!isCalendarAvailable()) {
    console.log('📅 Calendar non disponible, skip create');
    return null;
  }

  // Ne pas re-créer les événements importés de Google
  if (task.isImported || task.googleEventId) {
    console.log('📅 Événement déjà importé de Google, skip');
    return null;
  }

  try {
    const result = await googleCalendarService.createEvent(task, date);

    if (result?.id) {
      // Sauvegarder le mapping
      eventMapping.set(task.id, result.id);
      saveMapping();
      console.log('📅 Événement créé dans Google Calendar:', result.id);
      return result;
    }
  } catch (error) {
    console.error('❌ Erreur création événement Calendar:', error);
  }

  return null;
}

/**
 * Mettre à jour un événement dans Google Calendar
 */
async function updateCalendarEvent(task, date) {
  if (!isCalendarAvailable()) {
    return null;
  }

  // Chercher l'ID Google
  const googleEventId = task.googleEventId || eventMapping.get(task.id);

  if (!googleEventId) {
    // Pas d'événement existant, en créer un nouveau
    return createCalendarEvent(task, date);
  }

  try {
    const [hours, minutes] = (task.time || '09:00').split(':').map(Number);
    const duration = task.duration || 30;

    const startDate = new Date(date);
    startDate.setHours(hours, minutes, 0, 0);

    const endDate = new Date(startDate);
    endDate.setMinutes(endDate.getMinutes() + duration);

    const event = {
      summary: `[${(task.category || 'tâche').toUpperCase()}] ${task.task || task.title}`,
      description: task.desc || (task.instructions?.map(i => getInstructionText(i)).filter(Boolean).join('\n')) || '',
      start: {
        dateTime: startDate.toISOString(),
        timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone
      },
      end: {
        dateTime: endDate.toISOString(),
        timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone
      }
    };

    const response = await window.gapi.client.calendar.events.update({
      calendarId: 'primary',
      eventId: googleEventId,
      resource: event
    });

    console.log('📅 Événement mis à jour dans Google Calendar:', googleEventId);
    return response.result;
  } catch (error) {
    // Si l'événement n'existe plus, essayer de le recréer
    if (error.status === 404) {
      console.log('📅 Événement non trouvé, création...');
      eventMapping.delete(task.id);
      saveMapping();
      return createCalendarEvent(task, date);
    }
    console.error('❌ Erreur mise à jour événement Calendar:', error);
  }

  return null;
}

/**
 * Supprimer un événement de Google Calendar
 */
async function deleteCalendarEvent(taskId, googleEventId = null) {
  if (!isCalendarAvailable()) {
    return false;
  }

  // Chercher l'ID Google
  const eventId = googleEventId || eventMapping.get(taskId);

  if (!eventId) {
    console.log('📅 Pas d\'événement Google associé à supprimer');
    return false;
  }

  try {
    await window.gapi.client.calendar.events.delete({
      calendarId: 'primary',
      eventId: eventId
    });

    // Nettoyer le mapping
    eventMapping.delete(taskId);
    saveMapping();

    console.log('📅 Événement supprimé de Google Calendar:', eventId);
    return true;
  } catch (error) {
    // 404 = déjà supprimé, c'est OK
    if (error.status === 404) {
      eventMapping.delete(taskId);
      saveMapping();
      return true;
    }
    console.error('❌ Erreur suppression événement Calendar:', error);
  }

  return false;
}

/**
 * Synchroniser tous les événements d'une date vers Google Calendar
 */
async function syncDayToCalendar(tasks, date) {
  if (!isCalendarAvailable()) {
    return { synced: 0, errors: [] };
  }

  const results = { synced: 0, errors: [] };

  for (const task of tasks) {
    try {
      const googleEventId = task.googleEventId || eventMapping.get(task.id);

      if (googleEventId) {
        // Mise à jour
        await updateCalendarEvent(task, date);
      } else if (!task.isImported) {
        // Création (seulement si pas importé de Google)
        await createCalendarEvent(task, date);
      }
      results.synced++;
    } catch (error) {
      results.errors.push({ taskId: task.id, error: error.message });
    }
  }

  return results;
}

/**
 * Obtenir l'ID Google Calendar associé à une tâche locale
 */
function getGoogleEventId(taskId) {
  return eventMapping.get(taskId) || null;
}

/**
 * Définir manuellement un mapping
 */
function setGoogleEventId(taskId, googleEventId) {
  eventMapping.set(taskId, googleEventId);
  saveMapping();
}

export default {
  isCalendarAvailable,
  createCalendarEvent,
  updateCalendarEvent,
  deleteCalendarEvent,
  syncDayToCalendar,
  getGoogleEventId,
  setGoogleEventId
};

export {
  isCalendarAvailable,
  createCalendarEvent,
  updateCalendarEvent,
  deleteCalendarEvent,
  syncDayToCalendar,
  getGoogleEventId,
  setGoogleEventId
};
